import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  fetchPaperEditions,
  fetchPublicPapers,
  type ConferenceEdition,
  type ConferencePaperPublic,
} from "./api";
import { formatDate } from "./format";

const PAGE_SIZE = 12;
const DEBOUNCE_MS = 280;

function languageLabel(language: string, t: ReturnType<typeof useLanguage>["t"]) {
  if (language === "ar") return t.papers.langArabic;
  if (language === "en") return t.papers.langEnglish;
  if (language === "fr") return t.papers.langFrench;
  return language;
}

function editionBadge(paper: ConferencePaperPublic, locale: string) {
  if (locale === "ar") {
    return `${paper.conference_title_ar} — ${paper.conference_year}`;
  }
  return `${paper.conference_title_en} — ${paper.conference_year}`;
}

export function PapersPage() {
  const { t, locale } = useLanguage();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [language, setLanguage] = useState("");
  const [editions, setEditions] = useState<ConferenceEdition[]>([]);
  const [selectedConferences, setSelectedConferences] = useState<number[]>([]);
  const [editionQuery, setEditionQuery] = useState("");
  const [editionOpen, setEditionOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<ConferencePaperPublic[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const editionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchPaperEditions()
      .then((res) => setEditions(res.data))
      .catch(() => setEditions([]));
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, language, selectedConferences]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!editionRef.current?.contains(e.target as Node)) setEditionOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPublicPapers({
      search: debouncedSearch || undefined,
      language: language || undefined,
      conference: selectedConferences.length ? selectedConferences : undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.meta.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : t.common.error);
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, language, selectedConferences, offset, t.common.error]);

  const filteredEditions = useMemo(() => {
    const q = editionQuery.trim().toLowerCase();
    if (!q) return editions;
    return editions.filter((e) => {
      const hay = `${e.label_en} ${e.label_ar} ${e.number} ${e.year}`.toLowerCase();
      return hay.includes(q);
    });
  }, [editions, editionQuery]);

  const conferenceCountInResults = useMemo(() => {
    return new Set(rows.map((r) => r.conference_number)).size;
  }, [rows]);

  const selectedLabels = selectedConferences
    .map((n) => editions.find((e) => e.number === n))
    .filter(Boolean) as ConferenceEdition[];

  const toggleConference = (n: number) => {
    setSelectedConferences((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => b - a),
    );
  };

  const clearSearch = () => {
    setSearchInput("");
    setDebouncedSearch("");
  };

  const clearConferences = () => setSelectedConferences([]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const totalEditions = editions.length || 18;

  const summary =
    selectedConferences.length > 0 || debouncedSearch || language
      ? t.papers.filteredSummary
          .replace("{papers}", String(total))
          .replace(
            "{conferences}",
            String(selectedConferences.length || conferenceCountInResults || 0),
          )
      : t.papers.archiveSummary.replace("{conferences}", String(totalEditions));

  return (
    <div>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.papers.title}
      </h1>
      <p className="lead">{t.papers.lead}</p>
      <p className="dn-papers-summary">{summary}</p>

      <div className="dn-papers-toolbar dn-papers-toolbar-archive">
        <label className="dn-papers-search">
          <span className="dn-muted">{t.papers.search}</span>
          <div className="dn-papers-search-row">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t.papers.searchPlaceholder}
              aria-label={t.papers.search}
            />
            {searchInput && (
              <button type="button" className="dn-btn dn-btn-ghost" onClick={clearSearch}>
                {t.papers.clear}
              </button>
            )}
          </div>
        </label>

        <div className="dn-papers-conference" ref={editionRef}>
          <span className="dn-muted">{t.papers.filterConference}</span>
          <button
            type="button"
            className="dn-conference-trigger"
            onClick={() => setEditionOpen((o) => !o)}
            aria-expanded={editionOpen}
          >
            {selectedLabels.length === 0
              ? t.papers.conferenceAll
              : t.papers.conferenceSelected.replace("{count}", String(selectedLabels.length))}
          </button>
          {editionOpen && (
            <div className="dn-conference-panel">
              <input
                type="search"
                value={editionQuery}
                onChange={(e) => setEditionQuery(e.target.value)}
                placeholder={t.papers.conferenceSearch}
                aria-label={t.papers.conferenceSearch}
              />
              <div className="dn-conference-options">
                {filteredEditions.map((e) => {
                  const label = locale === "ar" ? e.label_ar : e.label_en;
                  const checked = selectedConferences.includes(e.number);
                  return (
                    <label key={e.number} className="dn-conference-option">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleConference(e.number)}
                      />
                      <span>
                        {label}
                        <span className="dn-muted"> ({e.paper_count})</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedConferences.length > 0 && (
                <button type="button" className="dn-btn dn-btn-ghost" onClick={clearConferences}>
                  {t.papers.clearConferences}
                </button>
              )}
            </div>
          )}
          {selectedLabels.length > 0 && (
            <div className="dn-conference-chips">
              {selectedLabels.map((e) => (
                <button
                  key={e.number}
                  type="button"
                  className="dn-chip"
                  onClick={() => toggleConference(e.number)}
                >
                  {locale === "ar" ? e.label_ar : e.label_en} ×
                </button>
              ))}
            </div>
          )}
        </div>

        <label>
          <span className="dn-muted">{t.papers.filterLanguage}</span>
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="">{t.papers.langAll}</option>
            <option value="en">{t.papers.langEnglish}</option>
            <option value="ar">{t.papers.langArabic}</option>
            <option value="fr">{t.papers.langFrench}</option>
          </select>
        </label>
      </div>

      <p className="dn-muted" style={{ marginBottom: "1rem" }}>
        {loading ? t.common.loading : t.papers.results.replace("{count}", String(total))}
      </p>

      {error && <div className="dn-alert dn-alert-error">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="dn-card dn-papers-empty">
          <h3>{t.papers.emptyTitle}</h3>
          <p className="dn-muted">{t.papers.emptyBody}</p>
        </div>
      )}

      <div className="dn-papers-list">
        {rows.map((paper) => (
          <article key={paper.id} className="dn-card dn-paper-card">
            <div className="dn-paper-card-top">
              <h3>{locale === "ar" && paper.title_alt ? paper.title_alt : paper.title}</h3>
              <span className="dn-paper-edition">{editionBadge(paper, locale)}</span>
            </div>
            {paper.is_external_archive && (
              <p className="dn-paper-archive-badge">{t.papers.externalArchive}</p>
            )}
            {locale === "ar" && paper.title_alt && paper.title !== paper.title_alt && (
              <p className="dn-muted" dir="ltr">
                {paper.title}
              </p>
            )}
            {locale === "en" && paper.title_alt && (
              <p className="dn-muted" dir="rtl">
                {paper.title_alt}
              </p>
            )}
            <p>
              <strong>{t.papers.authors}:</strong> {paper.authors}
            </p>
            <p className="dn-muted">
              <strong>{t.papers.affiliation}:</strong> {paper.affiliation}
            </p>
            <div className="dn-paper-meta">
              <span>{languageLabel(paper.language, t)}</span>
              <span>
                {formatDate(paper.published_at || paper.created_at, locale)}
              </span>
            </div>
            {paper.keywords && (
              <p className="dn-paper-keywords">
                <strong>{t.papers.keywords}:</strong> {paper.keywords}
              </p>
            )}
            <div className="dn-paper-actions">
              {paper.has_pdf ? (
                <>
                  <Link className="dn-btn dn-btn-primary" to={`/papers/${paper.id}/read`}>
                    {t.papers.readOnline}
                  </Link>
                  <a
                    className="dn-btn dn-btn-ghost"
                    href={`/api/v1/conference/papers/public/${paper.id}/pdf?download=true`}
                  >
                    {t.papers.downloadPdf}
                  </a>
                </>
              ) : (
                <span className="dn-muted">{t.papers.pdfUnavailable}</span>
              )}
            </div>
          </article>
        ))}
      </div>

      {total > PAGE_SIZE && (
        <div className="dn-cta-row" style={{ marginTop: "1.25rem", alignItems: "center" }}>
          <button
            type="button"
            className="dn-btn dn-btn-ghost"
            disabled={offset <= 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            {t.papers.prev}
          </button>
          <span className="dn-muted">
            {t.papers.page.replace("{page}", String(page)).replace("{pages}", String(pageCount))}
          </span>
          <button
            type="button"
            className="dn-btn dn-btn-ghost"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            {t.papers.next}
          </button>
        </div>
      )}
    </div>
  );
}
